import React, { useState, useMemo, useCallback } from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AddSquareIcon,
  DeleteIcon,
} from "@hugeicons/core-free-icons";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/CommonElements/PageTitle";
import { TableC } from "@/CommonElements/TableC";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import BtnAsLink from "@/Component/BtnAsLink";
import { useSystem } from "@/utils/hooks/useLocation";
import ConfirmModal from "@/CommonElements/ConfirmModal";
import { Trans } from "react-i18next";
import { useIncidentReportsQuery, useDeleteIncidentReportMutation, useReportCreatorsQuery, useIncidentReportLookupsQuery } from "@/services/reports";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import usePermissions from "@/utils/hooks/usePermissions";
import { permissions } from "@/utils/constants";
import dayjs from "dayjs";
import { Edit02Icon, AttachmentIcon, ViewIcon } from "@hugeicons/core-free-icons/index";

const formatLookupName = (obj) =>
  obj?.name_en ?? obj?.name_ar ?? obj?.code ?? "—";
const emptyCell = "—";

const formatReportDateTime = (date) =>
  date ? dayjs(date).format("A hh:mm YYYY-MM-DD") : emptyCell;

const PAGE_SIZE = 10;

// Columns shown by default on first load (match reports table: pilgrim #, passport, name, nationality, campaign #, service center, report status, case type, report #); others via column arrange
const DEFAULT_VISIBLE_COLUMN_KEYS = [
  "pilgrim_number",
  "passport",
  "pilgrim_name",
  "nationality",
  "campaign_number",
  "service_center",
  "report_status",
  "case_type",
  "incident_number",
  "actions",
];

const Reports = () => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const {
    hasAnyPermission
  } = usePermissions();
  const { currentSystem } = useSystem();
  const system = currentSystem || "makkah";
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    reportId: null,
  });

  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  const incidentPerms = useMemo(() => {
    if (system === "makkah") {
      return {
        create: permissions.create_makkah_incident_reports,
        viewDetails: permissions.view_makkah_incident_reports_details,
        update: permissions.update_makkah_incident_reports,
        delete: permissions.delete_makkah_incident_reports,
      };
    }
    return {
      create: permissions.create_madina_incident_reports,
      viewDetails: permissions.view_madina_incident_reports_details,
      update: permissions.update_madina_incident_reports,
      delete: permissions.delete_madina_incident_reports,
    };
  }, [system]);

  const canCreateReport = hasAnyPermission([incidentPerms.create]);
  const canViewReport = hasAnyPermission([incidentPerms.viewDetails]);
  const canUpdateReport = hasAnyPermission([incidentPerms.update]);
  const canDeleteReport = hasAnyPermission([incidentPerms.delete]);

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      per_page: PAGE_SIZE,
      ...(debouncedValue?.trim() ? { search: debouncedValue.trim() } : {}),
      ...Object.fromEntries(
        Object.entries(filters).filter(
          ([_, value]) => value !== "" && value !== null && value !== undefined
        )
      ),
    }),
    [currentPage, debouncedValue, filters]
  );

  const { data, isLoading, isFetching } = useIncidentReportsQuery(queryParams, { system });
  const deleteReport = useDeleteIncidentReportMutation({ system });
  const { data: reportCreatorsRes } = useReportCreatorsQuery({ system });
  const { data: lookupsRes } = useIncidentReportLookupsQuery({ system });
  const reportCreatorOptions = useMemo(() => {
    const list = reportCreatorsRes?.data?.data ?? reportCreatorsRes?.data ?? [];
    const arr = Array.isArray(list) ? list : [];
    return arr.map((c) => ({
      value: c.id,
      label: getLocalizedText(c.name_ar, c.name_en) ?? c.name_en ?? c.name_ar ?? "",
    }));
  }, [reportCreatorsRes, getLocalizedText]);
  const caseTypeOptions = useMemo(() => {
    const items = lookupsRes?.data?.data?.case_types ?? lookupsRes?.data?.case_types ?? [];
    const list = Array.isArray(items) ? items : (items?.items ?? items?.data ?? []);
    return list.map((c) => ({
      value: c.id,
      label: getLocalizedText(c.name_ar, c.name_en) ?? c.name_en ?? c.name_ar ?? c.code ?? "",
    }));
  }, [lookupsRes, getLocalizedText]);
  const caseAuthorityOptions = useMemo(() => {
    const items = lookupsRes?.data?.data?.case_authorities ?? lookupsRes?.data?.case_authorities ?? [];
    const list = Array.isArray(items) ? items : (items?.items ?? items?.data ?? []);
    return list.map((c) => ({
      value: c.id,
      label: getLocalizedText(c.name_ar, c.name_en) ?? c.name_en ?? c.name_ar ?? c.code ?? "",
    }));
  }, [lookupsRes, getLocalizedText]);
  const reports = data?.data?.data ?? data?.data ?? [];
  const meta = data?.data?.meta ?? data?.meta ?? {};
  const total = meta?.total ?? 0;
  const loading = isLoading || isFetching;

  const formatReportDate = useCallback((reportedAt, createdAt) => {
    const date = reportedAt || createdAt;
    if (!date) return emptyCell;
    return dayjs(date).format("YYYY-MM-DD HH:mm");
  }, []);

  const handleSearchChange = (e) => {
    setSearchValue(e?.target?.value ?? "");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterApply = (filterData) => {
    setFilters(filterData);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const handleDeleteClick = useCallback((reportId) => {
    setDeleteModalState({ isOpen: true, reportId });
  }, []);

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, reportId: null });
  };

  const handleDeleteConfirm = () => {
    const reportId = deleteModalState.reportId;
    if (!reportId) {
      handleDeleteCancel();
      return;
    }
    deleteReport.mutate(reportId, {
      onSuccess: () => {
        setDeleteModalState({ isOpen: false, reportId: null });
      },
    });
  };

  const getActions = useCallback(
    (rowData) => {
      const parts = [];
      if (canViewReport) {
        parts.push({
          label: (
            <Link
              to={`/app/${currentSystem}/reports/${rowData.id}/view`}
              className="d-flex align-items-center gap-2"
            >
              <HugeiconsIcon size={18} icon={ViewIcon} />
              <span>{t("VIEW")}</span>
            </Link>
          ),
          key: "view",
        });
      }
      if (canUpdateReport) {
        parts.push({
          label: (
            <Link
              to={`/app/${currentSystem}/reports/${rowData.id}/edit`}
              className="d-flex align-items-center gap-2"
            >
              <HugeiconsIcon size={18} icon={Edit02Icon} />
              <span>{t("EDIT")}</span>
            </Link>
          ),
          key: "edit",
        });
      }
      if (canDeleteReport) {
        parts.push({
          label: (
            <div className="d-flex align-items-center gap-2">
              <HugeiconsIcon size={18} icon={DeleteIcon} />
              <span>{t("DELETE")}</span>
            </div>
          ),
          key: "delete",
          onClick: () => handleDeleteClick(rowData.id),
          danger: true,
        });
      }
      return parts.reduce((acc, item, i) => {
        if (i > 0) acc.push({ type: "divider" });
        acc.push(item);
        return acc;
      }, []);
    },
    [currentSystem, t, handleDeleteClick, canViewReport, canUpdateReport, canDeleteReport]
  );

  // Column order matches table: Pilgrim #, Passport, Name, Nationality, Campaign #, Service center, Location, Status, Report #, then optional columns, then Actions
  const columns = useMemo(
    () => [
      {
        name: t("PILGRIM_NUMBER"),
        key: "pilgrim_number",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("pilgrim_number"),
        filterable: true,
        render: (_, rowData) => rowData?.pilgrim?.border_number ?? emptyCell,
      },
      {
        name: t("PASSPORT_NUMBER"),
        key: "passport",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("passport"),
        filterable: true,
        render: (_, rowData) => rowData?.pilgrim?.passport_number ?? emptyCell,
      },
      {
        name: t("PILGRIM_NAME"),
        key: "pilgrim_name",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("pilgrim_name"),
        filterable: true,
        render: (_, rowData) => (rowData?.reported_person_name ||
          getLocalizedText(rowData?.pilgrim?.full_name_ar, rowData?.pilgrim?.full_name_en)) ?? emptyCell,
      },
      {
        name: t("NATIONALITY"),
        key: "nationality",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("nationality"),
        filterable: true,
        render: (_, rowData) =>
          getLocalizedText(rowData?.pilgrim?.nationality_ar, rowData?.pilgrim?.nationality_en) ?? emptyCell,
      },
      {
        name: t("CAMPAIGN_OR_GROUP_NUMBER"),
        key: "campaign_number",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("campaign_number"),
        filterable: true,
        render: (_, rowData) => rowData?.pilgrim?.campaign_number ?? rowData?.campaign_number ?? emptyCell,
      },
      {
        name: t("SERVICE_CENTER"),
        key: "service_center",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("service_center"),
        filterable: true,
        render: (value, rowData) =>
          rowData?.pilgrim?.service_center?.name ??
          emptyCell,
      },
      {
        name: t("CASE_LOCATION"),
        key: "body_location",
        visible: false,
        filterable: true,
        render: (value, rowData) =>
          getLocalizedText(rowData?.body_location?.name_ar, rowData?.body_location?.name_en) ?? emptyCell,
      },
      {
        name: t("REPORT_STATUS"),
        key: "report_status",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("report_status"),
        filterable: true,
        render: (_, rowData) => {
          const isResolved = !!rowData?.resolved_at;
          return (
            <span
              className={
                isResolved
                  ? "badge badge-light-success"
                  : "badge badge-light-warning"
              }
            >
              {isResolved ? t("RESOLVED") : t("UNRESOLVED")}
            </span>
          );
        },
      },
      {
        name: t("STATUS"),
        key: "case_type",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("case_type"),
        filterable: true,
        render: (value, rowData) => getLocalizedText(rowData?.case_type?.name_ar, rowData?.case_type?.name_en) ?? value ?? emptyCell,
      },
      {
        name: t("REPORT_NUMBER"),
        key: "incident_number",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("incident_number"),
        filterable: true,
        render: (value, rowData) => {
          const num = value ?? rowData?.id ?? emptyCell;
          if (!canViewReport) return num;
          return (
            <Link className="fw-bold" to={`/app/${currentSystem}/reports/${rowData?.id}/view`}>
              {num}
            </Link>
          );
        },
      },
      {
        name: t("CASE_DESCRIPTION"),
        key: "description",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("description"),
        filterable: true,
        render: (value) =>
          value ? String(value).slice(0, 80) + (String(value).length > 80 ? "…" : "") : emptyCell,
      },
      {
        name: t("CASE_AUTHORITY"),
        key: "case_authority",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("case_authority"),
        filterable: true,
        render: (value, rowData) => getLocalizedText(rowData?.case_authority?.name_ar, rowData?.case_authority?.name_en) ?? value ?? emptyCell,
      },
      {
        name: t("REPORT_DATE"),
        key: "reported_at",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("reported_at"),
        filterable: true,
        render: (value) => formatReportDateTime(value),
      },
      {
        name: t("REPORT_CREATOR"),
        key: "creator",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("creator"),
        filterable: true,
        render: (value, rowData) => getLocalizedText(rowData?.creator?.name_ar, rowData?.creator?.name_en) ?? value ?? emptyCell,
      },
      {
        name: t("CASE_DATE"),
        key: "case_date",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("case_date"),
        filterable: true,
        render: (value, rowData) => formatReportDate(value ?? rowData?.reported_at) || emptyCell,
      },
      {
        name: t("END_DATE"),
        key: "resolved_at",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("resolved_at"),
        filterable: true,
        render: (value) => formatReportDateTime(value),
      },
      {
        name: t("CASE_ENDED_BY"),
        key: "resolved_by",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("resolved_by"),
        filterable: true,
        render: (value, rowData) =>
          formatLookupName(rowData?.resolved_by) ?? getLocalizedText(rowData?.resolved_by?.name_ar, rowData?.resolved_by?.name_en) ?? value ?? emptyCell,
      },
      {
        name: t("ATTACHMENTS"),
        key: "documents",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("documents"),
        filterable: true,
        render: (value) => {
          const list = Array.isArray(value) ? value : [];
          if (list.length === 0) return emptyCell;
          return (
            <div className="d-flex flex-column gap-1">
              {list.map((url, idx) => {
                const name = url.split("/").pop() || url.split("\\").pop() || t("ATTACHMENT");
                return (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none d-inline-flex align-items-center gap-1"
                  >
                    <HugeiconsIcon icon={AttachmentIcon} size={14} className="text-secondary" />
                    <span className="text-truncate" style={{ maxWidth: 140 }} title={name}>
                      {name}
                    </span>
                  </a>
                );
              })}
            </div>
          );
        },
      },
      {
        name: t("ACTIONS"),
        key: "actions",
        filterable: false,
        render: (value, rowData) => getActions(rowData),
      },
    ],
    [t, currentSystem, getActions, formatReportDate, getLocalizedText, canViewReport]
  );

  const filterFields = useMemo(
    () => [
      { name: "border_number", label: t("PILGRIM_NUMBER"), type: "input", placeholder: t("ENTER_PLACEHOLDER"), colSize: { md: 6 } },
      { name: "resolved_at", label: t("REPORT_STATUS"), type: "select", placeholder: t("CHOOSE"),
         options: [{ value: "true", label: t("RESOLVED") }, { value: "false", label: t("UNRESOLVED") }], colSize: { md: 6 } },
      { name: "from_date", label: t("FROM_DATE2"), type: "date", placeholder: t("ENTER_PLACEHOLDER"), colSize: { md: 6 } },
      { name: "to_date", label: t("TO_DATE"), type: "date", placeholder: t("ENTER_PLACEHOLDER"), colSize: { md: 6 } },
      {
        name: "company_id",
        label: t("LOCATION"),
        type: "select",
        placeholder: t("CHOOSE"),
        options: [{ value: "makkah", label: t("MAKKAH") }, { value: "madina", label: t("MADINAH") }],
        colSize: { md: 6 },
      },
      { name: "incident_number", label: t("REPORT_NUMBER"), type: "input", placeholder: t("ENTER_PLACEHOLDER"), colSize: { md: 6 } },
      {
        name: "case_type_id",
        label: t("CASE_TYPE"),
        type: "select",
        placeholder: t("CHOOSE"),
        options: caseTypeOptions,
        colSize: { md: 6 },
        showSearch: true,
        filterOption: (input, option) =>
          (option?.label ?? "").toString().toLowerCase().includes((input ?? "").toLowerCase()),
      },
      { name: "creator_id", label: t("REPORT_CREATOR"), type: "select", placeholder: t("CHOOSE"), options: reportCreatorOptions, colSize: { md: 6 } },
      {
        name: "nationality_id",
        label: t("NATIONALITY"),
        type: "select",
        optionKey: "nationalities",
        placeholder: t("CHOOSE"),
        options: (n) => ({ value: n.id, label: getLocalizedText(n.name_ar, n.name_en) ?? n.name }),
        colSize: { md: 6 },
      },
      {
        name: "case_authority_id",
        label: t("CASE_AUTHORITY"),
        type: "select",
        placeholder: t("CHOOSE"),
        options: caseAuthorityOptions,
        colSize: { md: 6 },
      },
      {
        name: "service_center_id",
        label: t("SERVICE_CENTER"),
        type: "select",
        optionKey: "service_centers",
        placeholder: t("CHOOSE"),
        options: (s) => ({
          value: s.id,
          label: s.name ?? "",
        }),
        colSize: { md: 6 },
      },
      { name: "campaign_or_group_number", label: t("CAMPAIGN_OR_GROUP_NUMBER"), type: "input", placeholder: t("ENTER_PLACEHOLDER"), colSize: { md: 6 } },
      { name: "gender", label: t("GENDER"), type: "select", placeholder: t("CHOOSE"), options: [{ value: "male", label: t("MALE") }, { value: "female", label: t("FEMALE") }], colSize: { md: 6 } },
    ],
    [t, getLocalizedText, reportCreatorOptions, caseTypeOptions, caseAuthorityOptions]
  );

  return (
    <div>

      <PageHeader title={t("REPORTS")} sticky={false}>
        {canCreateReport && (
          <BtnAsLink
            className="btn-primary"
            icon={<HugeiconsIcon icon={AddSquareIcon} />}
            link={`/app/${currentSystem}/reports/create`}
          >
            {t("NEW_REPORT")}
          </BtnAsLink>
        )}
      </PageHeader>
      <Container fluid>
        <TableC
          data={reports}
          columns={columns}
          loading={loading}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          searchPlaceholder={t("REPORTS_SEARCH_PLACEHOLDER")}
          pagination={
            total > 0
              ? {
                current: meta?.current_page ?? currentPage,
                total,
                pageSize: meta?.per_page ?? PAGE_SIZE,
                onChange: handlePageChange,
              }
              : undefined
          }
          actions={getActions}
          filterFields={filterFields}
          filters={filters}
          onFilterApply={handleFilterApply}
          onFilterReset={handleFilterReset}
          emptyStateTitle={debouncedValue ? t("NO_RESULTS") : t("NO_DATA")}
          emptyStateDescription={
            debouncedValue ? t("NO_RESULTS_DESCRIPTION") : t("NO_DATA_DESCRIPTION")
          }
        />
      </Container>

      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_REQUEST_MESSAGE"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />
    </div>
  );
};

export default Reports;
